import type { PropsWithChildren } from 'react';
import { useMemo } from 'react';

import { ChevronRight } from 'lucide-react';
import { twMerge } from 'tailwind-merge';

import { Button, Sheet, SheetTrigger } from '@/_components';
import { useSidebar } from '@/_components/sidebar';
import { useBoolean, useIsMobile } from '@/_shared';
import type { RouteListItem } from '@/types';

import { SecondaryMobileNavbar } from './mobile-navbar';
import { SecondaryNavbar } from './navbar';
import { SecondarySidebarProvider } from './sidebar-context-provider';

import { ContainerLayout } from '../containers';

const SECONDARY_DRAWER_BREAKPOINT = 768;

export function SecondaryPageLayout({ children, ...props }: PropsWithChildren<{ subNavLinks: RouteListItem[] }>) {
  const [sidebarOpen, setSidebarOpen] = useBoolean();
  const { state, isMobile: isPrimaryMobile } = useSidebar();
  const isFullWidth = state === 'expanded';
  const isCompact = useIsMobile(SECONDARY_DRAWER_BREAKPOINT);

  const triggerPositionClassName = useMemo(
    () =>
      twMerge(
        'fixed top-36 left-0 z-20 block',
        isFullWidth ? 'left-(--sidebar-width)' : 'left-(--sidebar-width-icon)',
        isPrimaryMobile ? 'left-0' : ''
      ),
    [isFullWidth, isPrimaryMobile]
  );

  return (
    <SecondarySidebarProvider value={[sidebarOpen, setSidebarOpen]}>
      <article className="flex flex-1">
        <aside>
          {isCompact ? (
            <Sheet
              open={sidebarOpen}
              onOpenChange={(open) => {
                setSidebarOpen.set(open);
              }}
            >
              <SecondaryMobileNavbar {...props} setSidebarOpen={setSidebarOpen.setFalse} isFullWidth={isFullWidth} />

              <div className={triggerPositionClassName}>
                <SheetTrigger>
                  <Button
                    tag="div"
                    icon={ChevronRight}
                    aria-label="secondary nav open button"
                    size="md"
                    className="rounded-l-none"
                    onClick={setSidebarOpen.setTrue}
                  />
                </SheetTrigger>
              </div>
            </Sheet>
          ) : (
            <nav className="sticky top-16 w-28">
              <div className="fixed">
                <SecondaryNavbar {...props} />
              </div>
            </nav>
          )}
        </aside>
        <ContainerLayout>{children}</ContainerLayout>
      </article>
    </SecondarySidebarProvider>
  );
}
